import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { dictionaries, LANGS } from "./dictionaries";

const STORAGE_KEY = "ms_lang";
const DEFAULT_LANG = "it";

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (k) => k,
});

/**
 * Resolves a nested key path like "home.hero.title" against a dictionary object.
 * Returns the resolved value or the key itself if not found.
 */
function resolvePath(dict, path) {
  if (!dict) return undefined;
  const parts = path.split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && dictionaries[saved]) return saved;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("i18n: unable to read language preference", err);
      }
    }
    return DEFAULT_LANG;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.setAttribute("lang", lang);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("i18n: unable to persist language preference", err);
      }
    }
  }, [lang]);

  const setLang = useCallback((code) => {
    if (dictionaries[code]) setLangState(code);
  }, []);

  const t = useCallback(
    (key, fallback) => {
      const value = resolvePath(dictionaries[lang], key);
      if (value !== undefined) return value;
      const it = resolvePath(dictionaries.it, key);
      if (it !== undefined) return it;
      return fallback !== undefined ? fallback : key;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, langs: LANGS }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}
